import React, { useState } from 'react';
import type { Theme } from '@material-ui/core';
import {
    FormControlLabel,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
} from '@material-ui/core';
import { FieldArray, useFormikContext } from 'formik';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useNokkeltallSammendrag } from '../kostnadskalkyle/context/useNokkeltallSammendrag';
import { LonnsomhetTableRow } from './LonnsomhetTableRow';
import { Block } from '@klp/design/Block';
import { ActionButton } from '@klp/design/Action';
import type { LonnsomhetDelsum } from '@klp/prosjekt/services/types';
import { LonnsomhetTableRowSummary } from './LonnsomhetTableRowSummary';
import { useTranslation } from 'react-i18next';
import { Typography } from '@klp/design/index';
import { NumberFormatted } from '@klp/formgenerator/components/NumberFormatted';
import { useKostnadsKalkyleContext } from '../kostnadskalkyle/context/KostnadsKalkyleProvider';
import { MVA_DELSUM_NAVN } from './mvaDelsum';

interface FormValues {
    delsummer: Array<LonnsomhetDelsum>;
}

const styles = (theme: Theme) => ({
    tableHead: {},
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: theme.spacing(2),
    },
    cell: {
        '&.navn': { maxWidth: '35%' },
        '&.investeringskostnad': { width: '15%' },
        '&.avskrivningstid': { width: '15%' },
        '&.restverdi': { width: '15%' },
        '&.aarligOktLeiepris': { width: '10%' },
        '&.oktLeieprisPerM2': { width: '10%' },
    },
    itemText: {
        display: 'flex',
        justifyContent: 'space-between',
        fontWeight: 400,
        fontSize: theme.klpVariant.epsilon.fontSize,
    },
    valueText: {
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
});

export const LonnsomhetTable: React.FC = () => {
    const { t } = useTranslation();
    const { nokkeltall, nokkeltallIsLoading, addDelsum } =
        useNokkeltallSammendrag();
    const { isReadOnly } = useKostnadsKalkyleContext();
    const classes = useKlpStyles({}, styles);
    const { values } = useFormikContext<FormValues>();
    // Display-only, non-persisted: true => all-in nettoleie; false => excludes mva line
    const [inkluderMva, setInkluderMva] = useState(true);

    if (!nokkeltall || !values?.delsummer) {
        return (
            <Typography
                padding="compact"
                translation="prosjekt.lonnsomhet.table.noData"
            />
        );
    }

    // Without an MVA row, nettoleie and nettoleieUtenMvaKompensasjon coincide —
    // the toggle would do nothing, so it is hidden (e.g. 100 % mva-pliktig, or a
    // completed schema without a persisted MVA row).
    const hasMvaDelsum = values.delsummer.some(
        (delsum) => delsum.navn === MVA_DELSUM_NAVN,
    );

    return (
        <FieldArray name="delsummer">
            {(arrayHelpers) => (
                <Table>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={`${classes.cell} navn`}>
                                {t('prosjekt.lonnsomhet.table.navn')}
                            </TableCell>
                            <TableCell
                                className={`${classes.cell} investeringskostnad`}
                            >
                                {t(
                                    'prosjekt.lonnsomhet.table.investeringskostnad',
                                )}
                            </TableCell>
                            <TableCell
                                className={`${classes.cell} avskrivningstid`}
                            >
                                {t('prosjekt.lonnsomhet.table.avskrivningstid')}
                            </TableCell>
                            <TableCell className={`${classes.cell} restverdi`}>
                                {t('prosjekt.lonnsomhet.table.restverdi')}
                            </TableCell>
                            <TableCell
                                className={`${classes.cell} aarligOktLeiepris`}
                            >
                                {t(
                                    'prosjekt.lonnsomhet.table.aarligOktLeiepris',
                                )}
                            </TableCell>
                            <TableCell
                                className={`${classes.cell} oktLeieprisPerM2`}
                            >
                                {t(
                                    'prosjekt.lonnsomhet.table.oktLeieprisPerM2',
                                )}
                            </TableCell>
                            <TableCell
                                className={`${classes.cell} actions`}
                                aria-label="Handlinger"
                            />
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {nokkeltall.delsummer.map((delsum, index) => (
                            <LonnsomhetTableRow
                                key={index}
                                index={index}
                                rowKey={`delsummer.${index}`}
                                data={delsum}
                                onRemove={() => arrayHelpers.remove(index)}
                                isLoading={nokkeltallIsLoading}
                            />
                        ))}
                        <LonnsomhetTableRowSummary data={nokkeltall.totalsum} />
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={1}>
                                <Typography className={classes.itemText}>
                                    {t('prosjekt.lonnsomhet.table.nettoLeie')}
                                    :
                                    <NumberFormatted
                                        className={classes.valueText}
                                        value={
                                            inkluderMva
                                                ? nokkeltall.nettoleie
                                                : nokkeltall.nettoleieUtenMvaKompensasjon
                                        }
                                    />
                                </Typography>
                            </TableCell>
                            <TableCell colSpan={3}>
                                {hasMvaDelsum && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={inkluderMva}
                                                onChange={(_e, checked) =>
                                                    setInkluderMva(checked)
                                                }
                                                color="primary"
                                                data-testid="netto-leie-mva-toggle"
                                            />
                                        }
                                        label={t(
                                            'prosjekt.lonnsomhet.table.inkluderMvaKompensasjon',
                                        )}
                                    />
                                )}
                            </TableCell>
                            <TableCell colSpan={2}>
                                <Block className={classes.footer}>
                                    {!isReadOnly && (
                                        <ActionButton
                                            actionType="button"
                                            variant="outlined"
                                            onClick={addDelsum}
                                            translation="prosjekt.lonnsomhet.table.leggTilDelprosjekt"
                                        />
                                    )}
                                </Block>
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            )}
        </FieldArray>
    );
};
